import React from "react";

import { useGetBenAdminRenewalQuery, useSignRenewalApplicationMutation, useUpdateBenAdminRenewalMutation } from "BenAdminRenewal/redux";
import { BenAdminRenewal, BenAdminRenewalUpdatableProps, SignatureProp } from "BenAdminRenewal/types";
import EmployerBreadcrumbs from "EmployerDashboard/Components/Breadcrumbs";
import {
  ContributionNameNoCol,
  ContributionTypeNoCol,
  EmployeePercentageNoCol,
  DependentPercentageNoCol,
  EmployeeFixedAmountNoCol,
  DependentFixedAmountNoCol,
} from "Features/FlexibleContributions";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import _ from "lodash";
import { Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import { validateRequired, validateRequiredSignature } from "Utils/Validations";

import Chrome from "../components/Chrome";
import Layout from "../components/Layout";
import { CardSection } from "Cobalt/Card";
import { TitleSmall } from "Cobalt/Header";
import { Table, HeaderTR, TH, SnugTH, TR, TD, SnugTD, FooterTR, FooterTD } from "Cobalt/TableV2";
import DashboardHeader from "Common/DashboardHeader";
import { SIFinalFormField, SIFormSelect } from "Common/forms";
import { Col, Row } from "Common/Grid";
import Icon from "Common/Icon";
import RemoveIcon from "Common/RemoveIcon";
import SignatureForm from "Common/Signature";
import BodyText from "Theme/BodyText";
import Button, { LinkButton } from "Theme/Button";

const focusOnErrors = createDecorator();

const contributionsRequired = (values) => {
  const activeContributions = values.filter((contribution) => contribution.active);
  if (activeContributions.length < 1) {
    return "At least one contribution is required";
  }
  return undefined;
};

const ContributionTable = ({ serviceLine, formInputName, tableValidations }) => {
  return (
    <FieldArray name={formInputName} validate={tableValidations}>
      {({ fields, meta }) => (
        <>
          <Table>
            <HeaderTR>
              <TH>Contribution Name</TH>
              <TH fitContent>
                <span style={{ display: "block", width: 225 }}>Contribution Type</span>
              </TH>
              <TH fitContent>
                <span style={{ display: "block", width: 100 }}>Employee</span>
              </TH>
              <TH fitContent align="left">
                <span style={{ display: "block", width: 100 }}>Dependent</span>
              </TH>
              <SnugTH>
                <span style={{ display: "block", width: 40 }}>&nbsp;</span>
              </SnugTH>
            </HeaderTR>
            {fields.map((inputName, index) => {
              const contribution = fields.value[index];
              const isPercentageType = contribution.contributionType !== "fixed";
              return (
                contribution.active && (
                  <TR key={index} highlightFirstCell={false}>
                    <TD>
                      <ContributionNameNoCol namePrefix={inputName} label={null} hideErrorText />
                    </TD>
                    <TD>
                      <ContributionTypeNoCol namePrefix={inputName} label={null} hideErrorText />
                    </TD>
                    <TD>
                      {isPercentageType ? (
                        <EmployeePercentageNoCol namePrefix={inputName} label={null} hideErrorText />
                      ) : (
                        <EmployeeFixedAmountNoCol namePrefix={inputName} label={null} hideErrorText />
                      )}
                    </TD>
                    <TD>
                      {isPercentageType ? (
                        <DependentPercentageNoCol namePrefix={inputName} label={null} hideErrorText />
                      ) : (
                        <DependentFixedAmountNoCol namePrefix={inputName} label={null} hideErrorText />
                      )}
                    </TD>
                    <SnugTD>
                      <Button
                        size="small"
                        color="link"
                        onClick={() => {
                          if (contribution.id) {
                            fields.update(index, { ...contribution, active: false });
                          } else {
                            fields.remove(index);
                          }
                        }}
                      >
                        <RemoveIcon />
                      </Button>
                    </SnugTD>
                  </TR>
                )
              );
            })}
            {typeof meta.error === "string" && (meta.dirty || meta.submitFailed) && (
              <TR>
                <TD colSpan={5} align="left">
                  <BodyText componentClass="span" className="text-error-900" weight="normal">
                    {meta.error}
                  </BodyText>
                </TD>
              </TR>
            )}
            <tfoot>
              <FooterTR>
                <FooterTD colSpan={5} align="left">
                  <Button size="small" px={0} color="link" onClick={() => fields.push({ serviceLine, active: true })}>
                    Add Contribution
                  </Button>
                </FooterTD>
              </FooterTR>
            </tfoot>
          </Table>
          {/* <BodyText>
            <strong>Form Key</strong>: {fields.name}
            <br />
            <strong>Form Key Value:</strong>:<pre>{JSON.stringify(fields.value, null, 2)}</pre>
            <strong>Form Key Meta:</strong>:<pre>{JSON.stringify(meta, null, 2)}</pre>
          </BodyText> */}
        </>
      )}
    </FieldArray>
  );
};

const ContributionSection = ({ serviceLine, formInputName, header }) => {
  const { data: benAdminRenewal } = useGetBenAdminRenewalQuery(null);
  const planIds = benAdminRenewal[`${_.camelCase(serviceLine)}PlanIds`] || [];
  const tableValidations = planIds.length > 0 ? contributionsRequired : undefined;
  return (
    <CardSection px={0} pb={0}>
      <TitleSmall mt={0} mb={3}>
        {header}
      </TitleSmall>
      <Row>
        <Col md={8}>
          <ContributionTable serviceLine={serviceLine} formInputName={formInputName} tableValidations={tableValidations} />
        </Col>
      </Row>
    </CardSection>
  );
};

type Data = {
  isLoading: boolean;
  benAdminRenewal: BenAdminRenewal;
};

const useData = (): Data => {
  const { isLoading: isLoadingBenAdminRenewal, data: benAdminRenewal } = useGetBenAdminRenewalQuery(null);

  return {
    benAdminRenewal,
    isLoading: isLoadingBenAdminRenewal,
  };
};

const formInitialValues = ({ benAdminRenewal }) => {
  const {
    probationaryPeriod,
    signatureImageDataUrl,
    medicalContributions,
    dentalContributions,
    visionContributions,
    lifeContributions,
    longTermDisabilityContributions,
    shortTermDisabilityContributions,
  } = benAdminRenewal;
  return {
    probationaryPeriod,
    signatureImageDataUrl,
    medicalContributions,
    dentalContributions,
    visionContributions,
    lifeContributions,
    longTermDisabilityContributions,
    shortTermDisabilityContributions,
  };
};
const PolicyDetailsPage: React.FC<RouteComponentProps<{}>> = () => {
  const { isLoading, benAdminRenewal } = useData();
  const [updateBenAdminRenewal] = useUpdateBenAdminRenewalMutation();
  const [signRenewalApplication] = useSignRenewalApplicationMutation();
  const history = useHistory();

  const submitHandler = async ({ signatureImageDataUrl, ...values }: BenAdminRenewalUpdatableProps & SignatureProp) => {
    const res = await updateBenAdminRenewal(values);
    if (res.error) {
      const { errors } = res.error.data;
      return errors;
    }
    const signatureRes = await signRenewalApplication({ id: benAdminRenewal.companyPolicyID, signatureImageDataUrl });
    if (signatureRes.error) {
      const { errors } = signatureRes.error.data;
      return errors;
    }
    history.push("/benefits-administration/renewal/employee-eligibility");
    return null;
  };

  const title = "Renewal Details";
  const crumbs = [
    { title: "Renewal", link: "/benefits-administration/renewal" },
    { title: "Details", link: "" },
  ];

  return (
    <Chrome title="SimplyInsured - Broker Dashboard">
      <Layout>
        {isLoading ? (
          <BodyText>Loading...</BodyText>
        ) : (
          <Form
            onSubmit={submitHandler}
            decorators={[focusOnErrors]}
            initialValues={formInitialValues({ benAdminRenewal })}
            mutators={{
              ...arrayMutators,
            }}
          >
            {({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <EmployerBreadcrumbs crumbs={crumbs} px={0} />
                <DashboardHeader title={title} px={0} />
                <CardSection px={0} pb={2}>
                  <Row>
                    <SIFinalFormField
                      sm={6}
                      name="probationaryPeriod"
                      label="Waiting Period For New Employees"
                      component={SIFormSelect}
                      componentProps={{
                        options: {
                          "1st of month after hire date": "1st of month after hire date",
                          "1 month": "1 month",
                          "2 months": "2 months",
                        },
                      }}
                      guidance={[
                        "Most companies choose ",
                        <strong key="waiting-period">'1st of month after hire date'</strong>,
                        ". This is the amount of time an employee needs to work before being eligible for insurance benefits.",
                      ]}
                      validate={validateRequired}
                      noBounce
                    />
                  </Row>
                </CardSection>
                <ContributionSection serviceLine="medical" formInputName="medicalContributions" header="Medical Contributions" />
                <ContributionSection serviceLine="dental" formInputName="dentalContributions" header="Dental Contributions" />
                <ContributionSection serviceLine="vision" formInputName="visionContributions" header="Vision Contributions" />
                <ContributionSection serviceLine="life" formInputName="lifeContributions" header="Life Contributions" />
                <ContributionSection
                  serviceLine="long_term_disability"
                  formInputName="longTermDisabilityContributions"
                  header="Long Term Disability Contributions"
                />
                <ContributionSection
                  serviceLine="short_term_disability"
                  formInputName="shortTermDisabilityContributions"
                  header="Short Term Disability Contributions"
                />
                <CardSection px={0} pb={0}>
                  <TitleSmall mt={0} mb={3}>
                    Authorize
                  </TitleSmall>
                  <BodyText mb={4}>
                    By signing below, I authorize SimplyInsured to send the provided information to the insurance company for the purpose of renewing coverage.
                  </BodyText>
                  <Row>
                    <SIFinalFormField
                      sm={6}
                      name="signatureImageDataUrl"
                      label={null}
                      component={SignatureForm}
                      componentProps={{
                        showSubmitButton: false,
                        clearButtonText: "Clear Signature",
                      }}
                      compact
                      noBounce={false}
                      validate={validateRequiredSignature}
                    />
                  </Row>
                </CardSection>
                <CardSection className="flex justify-between" px={0}>
                  <LinkButton component={Link} size="medium" to="/benefits-administration/renewal/overview" color="link" pl={0}>
                    <Icon icon="chevron-left" pr={3} />
                    Back
                  </LinkButton>
                  <Button type="submit" size="medium" disabled={submitting}>
                    Next
                  </Button>
                </CardSection>
              </form>
            )}
          </Form>
        )}
      </Layout>
    </Chrome>
  );
};

export default PolicyDetailsPage;
